import React, {
 useEffect, useCallback, useState, useRef 
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import PrintHeader from './PrintHeader';
import PrintFooter from './PrintFooter';
import { sessionActions } from '../../../store';
import './print.css';

/**
 * PrintLayout - Container principal para relatórios impressos
 *
 * Características:
 * - Header com logo da revenda e metadados
 * - Footer com paginação em todas as páginas
 * - Suporte a multi-página com thead repetindo
 * - CSS otimizado para impressão
 *
 * @param {Object} props
 * @param {string} props.title - Título do relatório
 * @param {string} [props.subtitle] - Subtítulo opcional
 * @param {string|Date} [props.from] - Data/hora inicial
 * @param {string|Date} [props.to] - Data/hora final
 * @param {string} [props.deviceName] - Nome do(s) dispositivo(s)
 * @param {React.ReactNode} props.children - Conteúdo do relatório
 * @param {Function} [props.onReady] - Callback quando pronto para imprimir
 * @param {'portrait'|'landscape'} [props.orientation='portrait'] - Orientação
 */
const PrintLayout = ({
  title,
  subtitle,
  from,
  to,
  deviceName,
  children,
  onReady,
  orientation = 'portrait',
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const revenda = useSelector((state) => state.session?.revenda);
  const server = useSelector((state) => state.session?.server);
  const user = useSelector((state) => state.session?.user);
  const [isReady, setIsReady] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('Preparando...');

  const primaryColor = theme.palette.primary.main;

  // Prioridade: revenda.title > revenda.name > revenda.fantasia > server.attributes.title > 'Rastreamento GPS'
  const revendaName = revenda?.title
    || revenda?.name
    || revenda?.fantasia
    || server?.attributes?.title
    || server?.attributes?.['ui.title']
    || 'Rastreamento GPS';
  const revendaPhone = revenda?.phone || revenda?.telefone || server?.attributes?.phone;
  const revendaEmail = revenda?.email || server?.attributes?.email;
  const generatedBy = user?.name || user?.email || '';

  // Mark as ready when logo loads or fails
  const handleReady = useCallback(() => {
    setIsReady(true);
  }, []);

  // Wait for ALL images to load before enabling print button
  // This ensures mini-map tiles are fully loaded
  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    let pollInterval;

    const checkAllImagesLoaded = () => {
      // Get all images in the print content
      const images = document.querySelectorAll('.print-layout img');
      const imageArray = Array.from(images);

      if (imageArray.length === 0) {
        // No images, mark as ready after short delay
        if (isMounted) setLoadingStatus('Pronto');
        timeoutId = setTimeout(() => {
          if (isMounted) setIsReady(true);
        }, 500);
        return;
      }

      // Count loaded vs total images
      const loadedImages = imageArray.filter((img) => img.complete && img.naturalHeight !== 0);
      const totalImages = imageArray.length;
      const loadedCount = loadedImages.length;

      if (isMounted) {
        setLoadingStatus(`Carregando mapas: ${loadedCount}/${totalImages}`);
      }

      // Check if all images are loaded
      if (loadedCount === totalImages) {
        if (isMounted) {
          setLoadingStatus('Pronto');
          setIsReady(true);
        }
        return;
      }

      // Wait for remaining images to load
      let newLoadedCount = loadedCount;

      const handleImageLoad = () => {
        newLoadedCount += 1;
        if (isMounted) {
          setLoadingStatus(`Carregando mapas: ${newLoadedCount}/${totalImages}`);
        }
        if (newLoadedCount >= totalImages && isMounted) {
          setLoadingStatus('Pronto');
          setIsReady(true);
        }
      };

      imageArray.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', handleImageLoad);
          img.addEventListener('error', handleImageLoad); // Count errors as "loaded" to not block
        }
      });

      // Poll for image loading status (backup for missed events)
      pollInterval = setInterval(() => {
        const currentLoaded = imageArray.filter((img) => img.complete).length;
        if (isMounted && currentLoaded !== newLoadedCount) {
          newLoadedCount = currentLoaded;
          setLoadingStatus(`Carregando mapas: ${newLoadedCount}/${totalImages}`);
        }
        if (currentLoaded >= totalImages && isMounted) {
          setLoadingStatus('Pronto');
          setIsReady(true);
          clearInterval(pollInterval);
        }
      }, 500);

      // Fallback timeout - mark ready after 8 seconds max
      // This prevents infinite waiting if some images fail
      timeoutId = setTimeout(() => {
        if (isMounted && !isReady) {
          setLoadingStatus('Pronto (alguns mapas podem não ter carregado)');
          setIsReady(true);
        }
      }, 8000);
    };

    // Start checking after a brief delay to let DOM render
    const initialDelay = setTimeout(checkAllImagesLoaded, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearTimeout(initialDelay);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isReady]);

  // Handle print button click
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Handle close/cancel button click
  const handleClose = useCallback(() => {
    dispatch(sessionActions.setPrinting(false));
  }, [dispatch]);

  // Aplicar CSS custom properties
  useEffect(() => {
    const root = document.documentElement;

    // Cor primária
    root.style.setProperty('--print-primary', primaryColor);

    // Orientação via style tag (mais confiável que CSS variable para @page)
    const styleId = 'print-orientation-style';
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.textContent = `
      @media print {
        @page {
          size: A4 ${orientation};
        }
      }
    `;

    return () => {
      root.style.removeProperty('--print-primary');
      if (styleTag) {
        styleTag.remove();
      }
    };
  }, [primaryColor, orientation]);

  // Definir título do documento
  useEffect(() => {
    if (typeof document !== 'undefined' && title) {
      const previousTitle = document.title;
      document.title = title;
      return () => {
        document.title = previousTitle;
      };
    }
    return undefined;
  }, [title]);

  // Handler quando logo carrega
  const handleLogoLoad = useCallback(() => {
    handleReady();
  }, [handleReady]);

  // Se logo falhar, ainda marca como pronto
  const handleLogoError = useCallback(() => {
    handleReady();
  }, [handleReady]);

  return (
    <div className="print-layout">
      {/* Toolbar - só aparece na tela, não na impressão */}
      <div className="print-toolbar no-print">
        <button
          className="print-toolbar__btn print-toolbar__btn--secondary"
          onClick={handleClose}
          type="button"
        >
          ✕ Fechar
        </button>
        <div className="print-toolbar__center">
          <span className="print-toolbar__title">{title}</span>
          {!isReady && (
            <span className="print-toolbar__status">{loadingStatus}</span>
          )}
        </div>
        <button
          className="print-toolbar__btn print-toolbar__btn--primary"
          onClick={handlePrint}
          disabled={!isReady}
          type="button"
        >
          🖨 Imprimir / Salvar PDF
        </button>
      </div>

      <PrintHeader
        title={title}
        subtitle={subtitle}
        from={from}
        to={to}
        deviceName={deviceName}
        companyName={revendaName}
        onLogoLoad={handleLogoLoad}
        onLogoError={handleLogoError}
      />

      <main className="print-content">
        {children}
      </main>

      <PrintFooter
        companyName={revendaName}
        phone={revendaPhone}
        email={revendaEmail}
        generatedBy={generatedBy}
      />
    </div>
  );
};

export default PrintLayout;
